import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardTitle,
  List,
  ListItem,
  Badge,
  Alert
} from '@one-thd/sui-atomic-components';
import { ShareWeb, Refresh } from '@one-thd/sui-icons';
import { CountdownTimer } from '@thd-olt-component-react/countdown-timer';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { EmailQuoteDrawer } from '@thd-olt-component-react/quote-details-header';
import { useConfigService } from '@thd-nucleus/experience-context';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useImpression } from '@thd-olt-component-react/impression';
import { useQuoteContext } from '../../hooks/useQuoteContext';
import { useEmailQuote } from '../../hooks/useEmailQuote';
import { quotePropType } from '../../utils/propTypes';
import { quoteTitleStyles } from '../../constants/responsiveClassNames';
import CallToActionButton from './CallToActionButton';
import { triggerAnalyticsCustomEvent } from '../../utils/analyticsEvents';

const QuotesDetailsBox = ({
  quote, fullWidth, lastSlide, index
}) => {
  const { componentId, isMobile } = useQuoteContext();
  const { svocID, userID } = useThdCustomer();
  const fsShowRefreshOptions = useConfigService('fs-prop:b2b-show-refresh-quote-options');
  const fsShowShareOptions = useConfigService('fs-prop:b2b-show-share-quote-options');
  const endDate = new Date(
    `${quote?.expiryDt}T23:59:59.999-04:00`
  );
  const quoteStatus = quote?.status?.toLowerCase();
  const expiredDateFormatted = endDate.toLocaleDateString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  }); // MM/DD/YY
  const millisecondsInADay = 24 * 60 * 60 * 1000; // One day in milliseconds
  const [timeLeftMs, setTimeLeftMs] = useState(endDate - new Date());
  const [isClient, setIsClient] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const showCountdown = quoteStatus !== 'expired' && timeLeftMs > 0 && timeLeftMs < millisecondsInADay;

  const [printOption, setPrintOption] = useState('priceanddiscounts');
  const invokedUseEmailQuote = useEmailQuote(printOption,
    () => {
      setShowSuccessAlert(true);
    },
    () => {}
  );

  const handleRefreshQuote = () => {
    // Placeholder function until we get true redirection into quotes experience
    const refreshAction = () => {
      // eslint-disable-next-line no-console
      console.log('Refresh Quote Action Triggered');
      triggerAnalyticsCustomEvent('quotes.stash-click', {
        id: quote?.createdAssociateId, // Define proper id for analytics
        component: 'refresh'
      });
    };
    LIFE_CYCLE_EVENT_BUS.trigger('quote.refreshQuote', refreshAction);
  };

  const handleShareQuote = () => {
    setShareModalOpen(true);
    triggerAnalyticsCustomEvent('quotes.email-overlay-view');
  };

  const callToActions = {
    active: {
      icon: ShareWeb,
      text: 'Share',
      onClick: handleShareQuote,
      testId: 'share-button'
    },
    expired: {
      icon: Refresh,
      text: 'Refresh',
      onClick: handleRefreshQuote,
      testId: 'refresh-button'
    }
  };

  useEffect(() => {
    setIsClient(true);
    const interval = setInterval(() => {
      setTimeLeftMs(endDate - new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [endDate]);

  const countDownTimer = (
    <CountdownTimer
      endDate={endDate}
      timebombThreshold={millisecondsInADay}
      shouldUseClockDisplay
    />
  );

  const { ref, clickID } = useImpression({
    data: {
      component: 'QuotesDetailsBox',
      name: quoteStatus === 'active' ? 'ActiveQuotesDetailsBox' : 'ExpiredQuotesDetailsBox',
      id: componentId,
      position: index + 1,
      type: 'content',
    }
  });

  return (
    <>
      <Card
        className={
          classNames({
            'sui-mr-4': !lastSlide && !fullWidth,
            'sui-w-full': fullWidth,
            'sui-w-72 @lg:sui-w-80 @3xl:sui-w-96': !fullWidth,
          },
          'sui-h-full')
        }
        CardContentProps={{
          className: classNames(
            'sui-h-full sui-flex sui-flex-col sui-grow',
            {
              'sui-p-3 sui-gap-2': isMobile,
              'sui-p-4 sui-gap-3': !isMobile
            }
          )
        }}
        data-testid="quote-details-box"
        ref={ref}
        clickid={clickID}
      >
        { quoteStatus === 'active' && showSuccessAlert && (
          <Alert
            status="success"
            onClose={() => setShowSuccessAlert(false)}
          >
            Email Sent Successfully
          </Alert>
        )}
        { !(quoteStatus === 'active' && showSuccessAlert) && (
          <div className="sui-flex sui-justify-between sui-items-center">
            <Badge
              variant="outline"
              color={quoteStatus === 'active' ? 'info' : 'primary'}
              size="small"
            >
              {quoteStatus === 'active' ? 'Active' : 'Expired'}
            </Badge>
            {((quoteStatus === 'active' && fsShowShareOptions)
              || (quoteStatus === 'expired' && fsShowRefreshOptions)) && isClient && (
              <CallToActionButton
                status={quoteStatus}
                callToActions={callToActions}
              />
            )}
          </div>
        )}
        <CardTitle
          header={(
            <Typography lineClamp="2" decoration="underline" component="h3" height="normal">
              <a
                href={`/quote/details/${quote.cartId}/customer/${svocID}/user/${quote?.createdUserId}`}
                color="primary"
                className={quoteTitleStyles}
              >
                {quote.name}
              </a>
            </Typography>
          )}
        />
        <div>
          <div data-testid="quote-expiration" className="sui-mb-2">
            {showCountdown && isClient ? (
              <Alert status="warning">
                <Typography variant="body-base" component="span">
                  <span className="sui-flex sui-items-center sui-gap-1 *:sui-font-normal">
                    Expires in {countDownTimer}
                  </span>
                </Typography>
              </Alert>
            ) : (
              <Typography variant="body-base" component="span">
                {quoteStatus === 'active'
                  ? `Expires on ${expiredDateFormatted}`
                  : `Expired on ${expiredDateFormatted}`}
              </Typography>
            )}
          </div>
          <List className="sui-flex sui-flex-col sui-gap-2">
            <ListItem className="sui-flex sui-gap-2">
              <Typography variant="body-base" weight="bold" component="span">
                <span className="sui-text-nowrap">PO/Job Name:</span>
              </Typography>
              <Typography truncate variant="body-base">{quote.poJobName}</Typography>
            </ListItem>
            <ListItem className={classNames('sui-gap-2 ', {
              'sui-hidden': fullWidth,
              'sui-flex': !fullWidth
            })}
            >
              <Typography variant="body-base" weight="bold" component="span">
                <span className="sui-text-nowrap">Subtotal:</span>
              </Typography>
              <Typography truncate variant="body-base">
                ${(quote.subTotal ?? 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </Typography>
            </ListItem>
          </List>
        </div>
      </Card>
      <ErrorBoundary name="email-quote-drawer">
        <EmailQuoteDrawer
          open={shareModalOpen}
          setOpen={setShareModalOpen}
          userId={userID}
          svocId={svocID}
          quoteId={quote.cartId}
          size="sm"
          printOption={printOption}
          setPrintOption={setPrintOption}
          useEmailQuote={invokedUseEmailQuote}
        />
      </ErrorBoundary>
    </>
  );
};

QuotesDetailsBox.propTypes = {
  quote: quotePropType,
  fullWidth: PropTypes.bool,
  lastSlide: PropTypes.bool,
  index: PropTypes.number
};

QuotesDetailsBox.defaultProps = {
  quote: {},
  fullWidth: false,
  lastSlide: false,
  index: 0
};

export default QuotesDetailsBox;
